import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { publicApi } from "@/lib/api";
import { toast } from "sonner";
import { Star } from "lucide-react";
import Navbar from "@/components/Navbar";

export default function FeedbackPage() {
  const [params] = useSearchParams();
  const code = (params.get("code") || "").toUpperCase();
  const email = params.get("email") || "";
  const [booking, setBooking] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reviewLinks, setReviewLinks] = useState({ google: "", facebook: "" });

  useEffect(() => {
    publicApi.get("/reviews/links").then(r => setReviewLinks(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!code || !email) return;
    publicApi
      .get(`/bookings/lookup?code=${encodeURIComponent(code)}&email=${encodeURIComponent(email)}`)
      .then((r) => {
        setBooking(r.data);
        if (r.data?.customer_rating) {
          setRating(r.data.customer_rating);
          setComment(r.data.customer_comment || "");
          setSubmitted(true);
        }
      })
      .catch(() => setBooking({ error: "Booking not found. Check the link." }));
  }, [code, email]);

  const submit = async () => {
    if (rating < 1) {
      toast.error("Please pick a star rating");
      return;
    }
    setBusy(true);
    try {
      await publicApi.post(`/bookings/${booking.booking_id}/feedback`, {
        rating, comment, code, email,
      });
      toast.success("Thank you for your feedback!");
      setSubmitted(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit feedback");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="public-light min-h-screen bg-[#050505] text-[#F5F2EB]">
      <Navbar />
      <section className="pt-32 pb-12 px-6 md:px-12">
        <div className="max-w-xl mx-auto">
          <div className="overline mb-2">Trip feedback</div>
          <h1 className="font-serif text-4xl text-[#D4AF37] mb-6">Rate your ride</h1>

          {!booking ? (
            <div className="text-[#A1A1AA] text-sm">Loading…</div>
          ) : booking.error ? (
            <div className="border border-red-700/30 bg-red-700/10 p-5 text-sm text-red-300">
              {booking.error}
              <div className="mt-3"><Link to="/" className="underline text-[#D4AF37]">Back home</Link></div>
            </div>
          ) : (
            <div className="border border-white/10 bg-[#0c0c0c] p-6">
              <div className="text-xs uppercase tracking-[0.2em] text-[#71717A] mb-3">
                Booking #{booking.confirmation_code}
              </div>
              <div className="text-sm text-[#A1A1AA] mb-1">
                {booking.pickup_address}
              </div>
              <div className="text-sm text-[#A1A1AA]">
                → {booking.dropoff_address || `${booking.hours}h hourly`}
              </div>
              <div className="text-xs text-[#71717A] mt-2">
                Chauffeur: {booking.assigned_driver_name || "—"} · {booking.vehicle_name}
              </div>

              <div className="border-t border-white/10 my-6"/>

              <div className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-3">
                How was your ride?
              </div>
              <div className="flex gap-2 mb-6">
                {[1,2,3,4,5].map(n => (
                  <button
                    key={n}
                    type="button"
                    data-testid={`star-${n}`}
                    disabled={submitted}
                    onMouseEnter={() => !submitted && setHover(n)}
                    onMouseLeave={() => !submitted && setHover(0)}
                    onClick={() => !submitted && setRating(n)}
                    className="transition-colors disabled:cursor-default"
                    aria-label={`${n} star`}
                  >
                    <Star
                      size={36}
                      strokeWidth={1.5}
                      className={(hover || rating) >= n ? "text-[#D4AF37] fill-[#D4AF37]" : "text-[#3a3a3a]"}
                    />
                  </button>
                ))}
              </div>

              <div className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">Comments (optional)</div>
              <textarea
                data-testid="feedback-comment"
                disabled={submitted}
                value={comment}
                onChange={e => setComment(e.target.value)}
                placeholder="Tell us about your experience…"
                rows={4}
                className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm text-[#F5F2EB] focus:border-[#D4AF37] outline-none mb-5"
              />

              {!submitted ? (
                <button
                  data-testid="submit-feedback"
                  disabled={busy || rating < 1}
                  onClick={submit}
                  className="btn-gold w-full justify-center"
                >
                  {busy ? "Sending…" : "Submit feedback"}
                </button>
              ) : (
                <>
                <div className="border border-[#D4AF37]/40 bg-[#D4AF37]/10 p-4 text-sm text-[#D4AF37] text-center">
                  Thank you — your {rating}-star rating has been received.
                </div>
                {rating >= 4 && (reviewLinks.google || reviewLinks.facebook) && (
                  <div className="mt-5 text-center" data-testid="public-review-nudge">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-3">Loved your ride? Share it publicly</div>
                    <div className="flex flex-col sm:flex-row gap-2 justify-center">
                      {reviewLinks.google && (
                        <a data-testid="google-review-nudge-btn" href={reviewLinks.google} target="_blank" rel="noreferrer"
                          className="border border-[#D4AF37] text-[#D4AF37] px-5 py-2.5 text-[11px] uppercase tracking-[0.16em] font-semibold hover:bg-[#D4AF37]/10">
                          ★ Review us on Google
                        </a>
                      )}
                      {reviewLinks.facebook && (
                        <a data-testid="facebook-review-nudge-btn" href={reviewLinks.facebook} target="_blank" rel="noreferrer"
                          className="border border-[#5b9bff]/60 text-[#5b9bff] px-5 py-2.5 text-[11px] uppercase tracking-[0.16em] font-semibold hover:bg-[#5b9bff]/10">
                          Recommend on Facebook
                        </a>
                      )}
                    </div>
                  </div>
                )}
                </>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
